/**
 * Artifact Service
 * Fetches agent-generated artifact detail (current doc_model + version history)
 * and handles downloads (native file, or on-demand HTML→PDF export).
 */

import api from '@services/api';
import { serverFileName, triggerBlobDownload } from '@/utils/download';
import type { OutputFormat, ArtifactVersion, DriveCopy, FormulaValues } from '@/types/models';

export interface ArtifactDetail {
  file_id: string;
  file_name: string;
  format: OutputFormat;
  version: number;
  title?: string;
  branded?: boolean;
  /** Which brand rendered these bytes. 'sandbox' = a bundle being TRIED on
   *  one conversation without being installed (brand_resolver.py). */
  brand_source?: 'tenant' | 'default' | 'draft' | 'sandbox' | null;
  /** Set on samples rendered while a brand was being authored. */
  draft_conversation_id?: string | null;
  doc_model?: any;
  /** Spreadsheets only: what each formula computes (see FormulaValues). */
  formula_values?: FormulaValues | null;
  render?: { mime_type?: string; storage_path?: string; rendered_at?: string };
  google_form?: { status?: string; form_id?: string; responder_uri?: string; edit_uri?: string };
  revision_history: ArtifactVersion[];
  drive_copies?: DriveCopy[];
  can_edit?: boolean;
}

/**
 * The file extension each artifact format actually produces.
 *
 * Typed as a full Record over OutputFormat on purpose: adding a format without
 * teaching this map is a COMPILE error, not a download named `.md`. That is
 * exactly how `xlsx` once shipped an Excel file under a markdown name, and how
 * `pdf` did it again.
 *
 * It is only a fallback. The server's Content-Disposition wins whenever it can
 * be read, because it comes from the renderer that produced the bytes
 * (`serverFileName` in @/utils/download).
 */
export const EXTENSION_FOR_FORMAT: Record<OutputFormat, string> = {
  markdown: 'md',
  docx: 'docx',
  pdf: 'pdf',
  pptx: 'pptx',
  xlsx: 'xlsx',
  html: 'html',
  gforms: 'gform',
  numbers: 'numbers',
  // the packaged design-skill bundle the brand-book agent delivers
  zip: 'zip',
};

export function extensionForFormat(fmt?: string): string {
  return EXTENSION_FOR_FORMAT[(fmt || '') as OutputFormat] ?? 'bin';
}

class ArtifactService {
  /** Every artifact a conversation produced, newest first.
   *
   *  Answers from the artifacts' own records rather than from message
   *  attachments, so a file whose turn never got bound to its message is
   *  still reachable when the chat is reopened. */
  async listForConversation(
    conversationId: string,
  ): Promise<{ artifact_file_id: string; title?: string; format?: string; version?: number }[]> {
    const res = await api.get(`/conversations/${conversationId}/artifacts`);
    return (res.data as any)?.artifacts || [];
  }

  /** Current model + version history for an artifact (drives the panel preview). */
  async getDetail(fileId: string): Promise<ArtifactDetail> {
    const res = await api.get(`/files/artifacts/${fileId}`);
    return res.data;
  }

  /** A specific past version's document model (for viewing an older revision). */
  async getVersion(fileId: string, version: number): Promise<{
    version: number; doc_model: any; formula_values?: FormulaValues | null; is_latest: boolean;
  }> {
    const res = await api.get(`/files/artifacts/${fileId}/versions/${version}`);
    return res.data;
  }

  /** Download the artifact's native render. Pass `version` to fetch a specific
   * past version (re-rendered on demand); omit for the current version. */
  async download(fileId: string, fileName: string, version?: number): Promise<void> {
    const res = await api.get(`/files/download-by-id/${fileId}`, {
      responseType: 'blob',
      params: version != null ? { version } : undefined,
    });
    // The server's name wins: it comes from the renderer that produced the
    // bytes, so it can never disagree with what is actually inside them.
    // `fileName` stays as the fallback for a response with no disposition.
    const disposition = (res.headers as Record<string, string> | undefined)?.['content-disposition'];
    triggerBlobDownload(res.data, serverFileName(disposition) || fileName);
  }

  /** Download an HTML-presentation artifact converted to PDF on the server. */
  async downloadAsPdf(fileId: string, baseName: string): Promise<void> {
    const res = await api.get(`/files/artifacts/${fileId}/export.pdf`, { responseType: 'blob' });
    triggerBlobDownload(res.data, `${baseName}.pdf`);
  }

  /** Permanently delete an artifact (storage binaries + DB row + attachments). */
  async remove(fileId: string): Promise<void> {
    await api.delete(`/files/artifacts/${fileId}`);
  }

  /** Delete a single version. Returns whether the whole artifact was removed
   *  (last version) and the new current version otherwise. */
  async removeVersion(fileId: string, version: number): Promise<{ artifact_deleted?: boolean; version?: number }> {
    const res = await api.delete(`/files/artifacts/${fileId}/versions/${version}`);
    return res.data || {};
  }
}

export const artifactService = new ArtifactService();
export default artifactService;
